import { z } from "zod";
import { Sha256 } from "../../common";

/**
 * One synthetic player, played by a player agent under a guest account of this name. The agent
 * follows its instructions as a person would: which perspective to ask for, when to arrive, step
 * away, or switch, and how to play.
 */
export const EvalCasePlayer = z.strictObject({
  name: z.string().min(1).max(100),
  agentVersionId: z.uuid(),
  /** Who the player is and what they do, in plain language. */
  instructions: z.string().min(1).max(20000),
});
export type EvalCasePlayer = z.infer<typeof EvalCasePlayer>;

/** One weighted part of a case's rubric. The grader scores it from 0 to 100. */
export const RubricSection = z.strictObject({
  key: z.string().regex(/^[a-z][a-z0-9-]*$/),
  name: z.string().min(1).max(100),
  /** What this section measures, with scoring anchors. The grader reads it verbatim. */
  description: z.string().min(1).max(5000),
  weight: z.number().positive(),
});
export type RubricSection = z.infer<typeof RubricSection>;

const Rubric = z
  .array(RubricSection)
  .min(1)
  .max(8)
  .refine(sections => new Set(sections.map(section => section.key)).size === sections.length, {
    message: "Each rubric section needs a different key.",
  });

export const EvalCase = z
  .strictObject({
    id: z.string().min(1).max(100),
    title: z.string().min(1).max(200),
    description: z.string().max(10000),
    presetVersionId: z.uuid(),
    sourceGameId: z.uuid().optional(),
    sourceRevision: z.number().int().nonnegative().optional(),
    /** An uploaded world the case starts from instead of the scenario or a saved game. */
    startingWorld: Sha256.optional(),
    players: z.array(EvalCasePlayer).min(1).max(8),
    graderVersionId: z.uuid(),
    rubric: Rubric,
    /** Play ends once this many jumps have completed. */
    rounds: z.number().int().min(1).max(100),
  })
  .refine(value => (value.sourceGameId === undefined) === (value.sourceRevision === undefined), {
    message: "A saved game needs both its id and revision.",
  })
  .refine(value => !(value.sourceGameId && value.startingWorld), {
    message: "A case starts from a saved game or an uploaded world, not both.",
  });
export type EvalCase = z.infer<typeof EvalCase>;

/**
 * An eval as it is checked in or pulled: the published shape, naming presets and agents by catalog
 * slug instead of version id. A slug means that item's live version; `slug@3` pins version 3.
 */
export const EvalFile = z.strictObject({
  name: z.string().min(1).max(200),
  description: z.string().max(10000),
  cases: z.array(
    z.strictObject({
      id: z.string().min(1).max(100),
      title: z.string().min(1).max(200),
      description: z.string().max(10000),
      preset: z.string().min(1),
      /** A world file to start from, relative to the eval file. */
      world: z.string().min(1).optional(),
      rounds: z.number().int().min(1).max(100),
      players: z
        .array(
          z.strictObject({
            name: z.string().min(1).max(100),
            agent: z.string().min(1),
            instructions: z.string().min(1).max(20000),
          }),
        )
        .min(1)
        .max(8),
      grader: z.string().min(1),
      rubric: Rubric,
    }),
  ),
});
export type EvalFile = z.infer<typeof EvalFile>;

/** What saving a draft replaces: its name, description, and every case. */
export const EvalDraft = z.strictObject({
  name: z.string().min(1).max(200),
  description: z.string().max(10000),
  cases: z.array(EvalCase).max(100),
});
export type EvalDraft = z.infer<typeof EvalDraft>;

export const EvalListing = z.object({
  id: z.uuid(),
  slug: z.string(),
  /** The live version's name, or its newest draft's before the first publish. */
  name: z.string(),
  description: z.string(),
  ownerId: z.uuid(),
  liveVersionId: z.uuid().nullable(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EvalListing = z.infer<typeof EvalListing>;

export const EvalVersionStatus = z.enum([
  "draft",
  "publishing",
  "published",
  "refused",
  "in_appeal",
  "removed",
]);
export type EvalVersionStatus = z.infer<typeof EvalVersionStatus>;

/** A published version, with the name and description it was published with. */
export const EvalVersion = z.object({
  id: z.uuid(),
  version: z.number().int(),
  label: z.string().nullable(),
  name: z.string(),
  description: z.string(),
  publishedAt: z.string(),
});
export type EvalVersion = z.infer<typeof EvalVersion>;

/** A draft its editors are working on, and its cases. */
export const EvalDraftDetails = z.object({
  id: z.uuid(),
  evalId: z.uuid(),
  status: EvalVersionStatus,
  createdFromVersionId: z.uuid().nullable(),
  name: z.string(),
  description: z.string(),
  cases: z.array(EvalCase),
  problems: z.array(z.string()).nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EvalDraftDetails = z.infer<typeof EvalDraftDetails>;

export const EvalDetail = z.object({
  eval: EvalListing,
  versions: z.array(EvalVersion),
  cases: z.array(EvalCase),
  /** The evaluation's drafts, newest first. Empty unless `canEdit`. */
  drafts: z.array(EvalDraftDetails),
  /** Each case's notes in markdown, by case id. They carry across versions. */
  caseNotes: z.record(z.string(), z.string()),
  authors: z.array(z.object({ id: z.uuid(), name: z.string(), username: z.string().nullable() })),
  canEdit: z.boolean(),
});
export type EvalDetail = z.infer<typeof EvalDetail>;

/** What the Game Master under test used during one trial, totaled when the trial finishes. */
export const EvalTrialUsage = z.object({
  costUsd: z.number().nonnegative(),
  inputTokens: z.number().int().nonnegative(),
  cachedInputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  modelCalls: z.number().int().nonnegative(),
  toolCalls: z.number().int().nonnegative(),
  /** How long the Game Master spent answering jumps and chats. */
  gameMasterMs: z.number().nonnegative(),
});
export type EvalTrialUsage = z.infer<typeof EvalTrialUsage>;

/** A grader's scores for one trial, one per rubric section. */
export const Scorecard = z.object({
  /** Weighted mean of section scores, computed by the API, never by the grader. */
  overall: z.number().min(0).max(100),
  summary: z.string().min(1),
  sections: z.array(
    z.object({
      key: z.string(),
      score: z.number().min(0).max(100),
      summary: z.string(),
      findings: z.array(
        z.object({
          revision: z.number().int().positive(),
          perspective: z.string().optional(),
          text: z.string().min(1),
        }),
      ),
    }),
  ),
});
export type Scorecard = z.infer<typeof Scorecard>;

export const EvalRun = z.object({
  id: z.uuid(),
  ownerId: z.uuid(),
  evalVersionId: z.uuid(),
  agentVersionId: z.uuid(),
  status: z.enum(["queued", "running", "completed", "failed", "cancelled"]),
  repetitions: z.number().int(),
  budgetCents: z.number().int(),
  /** What the run's owner and the evaluation's authors wrote about it, in markdown. */
  notes: z.string(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
});
const EvalTrial = z.object({
  id: z.uuid(),
  runId: z.uuid(),
  caseId: z.uuid(),
  repetition: z.number().int(),
  gameId: z.uuid().nullable(),
  status: z.enum(["queued", "running", "grading", "completed", "failed", "cancelled"]),
  scorecard: Scorecard.nullable(),
  usage: EvalTrialUsage.nullable(),
  /** Everything the trial's model calls cost: the Game Master's, the players' and the grader's. */
  spentUsd: z.number().nonnegative(),
  error: z.string().nullable(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
});
export const RunDetail = z.object({
  run: EvalRun,
  evaluation: z.object({ id: z.uuid(), name: z.string() }),
  agent: z.object({ id: z.uuid(), name: z.string(), version: z.number() }),
  trials: z.array(EvalTrial.extend({ caseKey: z.string(), caseTitle: z.string() })),
});
export type RunDetail = z.infer<typeof RunDetail>;

export const ResultPoint = z.object({
  caseKey: z.string(),
  caseTitle: z.string(),
  runId: z.uuid(),
  evalId: z.uuid(),
  evalName: z.string(),
  evalVersionId: z.uuid(),
  evalVersion: z.number(),
  agentId: z.uuid(),
  agentName: z.string(),
  agentVersionId: z.uuid(),
  agentVersion: z.number(),
  latest: z.boolean(),
  evalArchived: z.boolean(),
  agentArchived: z.boolean(),
  presetVersionId: z.uuid(),
  scenario: z.string(),
  games: z.number(),
  completed: z.number(),
  measuredGames: z.number(),
  /** The mean overall score across completed games. */
  score: z.number().nullable(),
  /** The mean score of each rubric section across completed games, by section key. */
  sections: z.record(z.string(), z.number().nullable()),
  cost: z.number().nullable(),
  /** The mean Game Master time per game, in seconds. */
  latency: z.number().nullable(),
  tokens: z.number().nullable(),
  cacheHit: z.number().nullable(),
  toolCalls: z.number().nullable(),
});
export type ResultPoint = z.infer<typeof ResultPoint>;
